import type { AgentMessage } from 'modules/coaching/types'

const MAXIMUM_RECONNECT_DELAY_MS = 5000
const INITIAL_RECONNECT_DELAY_MS = 250
const RECONNECT_BACKOFF_BASE = 2
const MAXIMUM_RECONNECT_EXPONENT = 5
const MAXIMUM_RANDOM_UINT32 = 4_294_967_295
const MAXIMUM_JITTER_MS = 250
const JITTER_DIVISOR = 4

export const getMessageSnapshotKey = (messages: AgentMessage[]): string => {
  return JSON.stringify(
    messages.map(message => ({
      contents: message.contents,
      id: message.id,
      interactionId: message.interactionId,
      role: message.role,
    }))
  )
}

export const shouldAdoptPersistedMessages = (
  currentMessages: AgentMessage[],
  persistedMessages: AgentMessage[]
): boolean => {
  if (persistedMessages.length === 0 || currentMessages.length > persistedMessages.length) {
    return false
  }
  return getMessageSnapshotKey(currentMessages) !== getMessageSnapshotKey(persistedMessages)
}

export const getReconnectDelay = (attempt: number): number => {
  const base = Math.min(
    MAXIMUM_RECONNECT_DELAY_MS,
    INITIAL_RECONNECT_DELAY_MS * RECONNECT_BACKOFF_BASE ** Math.min(attempt, MAXIMUM_RECONNECT_EXPONENT)
  )
  const randomValue = crypto.getRandomValues(new Uint32Array(1))[0]
  const jitterRatio = randomValue / MAXIMUM_RANDOM_UINT32
  return base + Math.floor(jitterRatio * Math.min(MAXIMUM_JITTER_MS, base / JITTER_DIVISOR))
}

export const waitForReconnect = async (milliseconds: number, signal: AbortSignal): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, milliseconds)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        reject(new DOMException('Aborted', 'AbortError'))
      },
      { once: true }
    )
  })
}
